
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { AdminCreatedUser } from './userAuthService';

// Service to handle operations with the admin_users table in Supabase
const staffService = {
  // Get all users
  getStaffUsers: async () => {
    try {
      console.log('Fetching users from database...');
      
      // Query admin_users table
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
      
      console.log('Retrieved users data:', data);
      
      // Handle empty result
      if (!data || data.length === 0) {
        console.log('No users found in database');
        return { users: [] };
      }
      
      // Format data for frontend
      const formattedUsers = data.map(user => ({
        id: user.id,
        username: user.username,
        role: user.role,
        email: user.email || null,
        fullName: user.full_name,
        permissions: user.permissions || [],
        createdAt: user.created_at,
        active: user.active
      }));
      
      console.log('Formatted users for frontend:', formattedUsers);
      
      return {
        users: formattedUsers
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      toast('Error', {
        description: 'Failed to fetch users. Please try again later.'
      });
      return { users: [] }; // Return empty array instead of throwing to prevent UI crash
    }
  },
  
  // Create new user - using edge function for password hashing
  createStaffUser: async (userData: AdminCreatedUser) => {
    try {
      console.log('Creating user with data:', { ...userData, password: '[REDACTED]' });
      
      // Insert directly to admin_users table - password will be hashed by the API
      const { data, error } = await supabase.functions.invoke('admin-auth/create-user', {
        body: userData
      });
      
      if (error) {
        console.error('Error creating user:', error);
        throw error;
      }
      
      console.log('User created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },
  
  // Get user by ID
  getStaffUserById: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select()
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      
      return {
        id: data.id,
        username: data.username,
        role: data.role,
        email: data.email || null,
        fullName: data.full_name,
        permissions: data.permissions || [],
        createdAt: data.created_at,
        active: data.active
      };
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  },
  
  // Update user
  updateStaffUser: async (userId: string, updates: Partial<AdminCreatedUser>) => {
    try {
      console.log(`Updating user ${userId} with:`, updates);
      
      const { error } = await supabase
        .from('admin_users')
        .update({
          role: updates.role,
          email: updates.email || null,
          full_name: updates.fullName,
          permissions: updates.permissions || [],
          active: updates.active !== undefined ? updates.active : true
        })
        .eq('id', userId);
      
      if (error) {
        console.error('Update error:', error);
        throw error;
      }
      
      console.log(`User ${userId} updated successfully`);
      return { message: 'User updated successfully' };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },
  
  // Delete user
  deleteStaffUser: async (userId: string) => {
    try {
      console.log(`Deleting user ${userId}`);
      
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', userId);
      
      if (error) {
        console.error('Delete error:', error);
        throw error;
      }
      
      console.log(`User ${userId} deleted successfully`);
      return { message: 'User deleted successfully' };
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },
  
  // Reset user password - using edge function for password hashing
  resetStaffPassword: async (userId: string, newPassword: string) => {
    try {
      console.log(`Resetting password for user ${userId}`);
      
      const { data, error } = await supabase.functions.invoke('admin-auth/reset-password', {
        body: { userId, newPassword }
      });
      
      if (error) {
        console.error('Password reset error:', error);
        throw error;
      }
      
      console.log(`Password reset successful for user ${userId}`);
      return data;
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw error;
    }
  }
};

export default staffService;
